/* MiniCalendar — small month-grid widget. Today highlighted, days
   with events get a dot, click to navigate to /v2/calendar. */

import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';

export interface MiniCalendarProps {
    events: SyncedCalendarEvent[];
    accent?: string;
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
}

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function startOfWeek(d: Date) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}

export function MiniCalendar({
    events,
    accent = '#fd775e',
    surface = 'rgba(255,255,255,0.65)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
}: MiniCalendarProps) {
    const navigate = useNavigate();
    const today = new Date();
    const monthName = today.toLocaleString(undefined, { month: 'long', year: 'numeric' });

    // Render 6 weeks centered on today's week, easier to fit a clean grid
    const start = startOfWeek(today);
    start.setDate(start.getDate() - 14); // 2 weeks before, 4 weeks total covers the area
    const days: Date[] = [];
    for (let i = 0; i < 6 * 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
    }

    const eventDays = new Set(
        events.map((e) => {
            const d = new Date(e.startDateTime);
            return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        }),
    );

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 16,
                border: `1px solid ${rule}`,
                color: text,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>{monthName}</span>
                <div style={{ display: 'flex', gap: 2 }}>
                    <button
                        type="button"
                        onClick={() => navigate('/v2/calendar')}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: muted,
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        aria-label="Previous month"
                    >
                        <ChevronLeft size={13} strokeWidth={2} />
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/v2/calendar')}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: muted,
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        aria-label="Next month"
                    >
                        <ChevronRight size={13} strokeWidth={2} />
                    </button>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 4 }}>
                {DAY_LABELS.map((d, i) => (
                    <div
                        key={i}
                        style={{
                            textAlign: 'center',
                            fontSize: 9.5,
                            color: muted,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            padding: '4px 0',
                        }}
                    >
                        {d}
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                {days.map((d, i) => {
                    const isToday =
                        d.getFullYear() === today.getFullYear() &&
                        d.getMonth() === today.getMonth() &&
                        d.getDate() === today.getDate();
                    const inMonth = d.getMonth() === today.getMonth();
                    const hasEvent = eventDays.has(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => navigate('/v2/calendar')}
                            style={{
                                position: 'relative',
                                aspectRatio: '1 / 1',
                                fontSize: 11,
                                background: isToday ? accent : 'transparent',
                                color: isToday ? '#fff' : inMonth ? text : muted,
                                fontWeight: isToday ? 700 : inMonth ? 500 : 400,
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                opacity: inMonth ? 1 : 0.4,
                            }}
                        >
                            {d.getDate()}
                            {hasEvent && !isToday && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        bottom: 4,
                                        width: 3,
                                        height: 3,
                                        borderRadius: '50%',
                                        background: accent,
                                    }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
